import { FC, FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TopHeader.module.scss';
import { clientContent } from '../../client/content';
import { AskAiDrawer } from '../AskAiDrawer';
import { PulseLogo } from '../PulseLogo';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface TopHeaderProps {
  onToggleSidebar?: () => void;
  breadcrumbs?: BreadcrumbItem[];
  searchPlaceholder?: string;
  sidebarOpen?: boolean;
}

export const TopHeader: FC<TopHeaderProps> = ({
  onToggleSidebar,
  breadcrumbs,
  searchPlaceholder = 'Search assets, drawings & documents',
  sidebarOpen,
}) => {
  const [isAskAiOpen, setIsAskAiOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/search');
    }
  };

  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: clientContent.systemName, href: '/' },
    { label: clientContent.clientName },
  ];

  const items = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs;

  return (
    <>
      <header className={styles.header}>
        <div className={styles.leftGroup}>
          {onToggleSidebar && (
            <button
              type="button"
              className={styles.menuButton}
              onClick={onToggleSidebar}
              aria-label={sidebarOpen ? 'Close navigation sidebar' : 'Open navigation sidebar'}
              aria-expanded={sidebarOpen}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}

          <div className={styles.mobileBrandLogo}>
            <a href="/" aria-label="Pulse Home">
              <PulseLogo variant="full" theme="light" />
            </a>
          </div>

          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === items.length - 1;

              return (
                <span key={`${item.label}-${index}`} className={styles.breadcrumbItem}>
                  {index > 0 && (
                    <span className={styles.divider} aria-hidden="true">
                      /
                    </span>
                  )}
                  {item.href ? (
                    <a
                      href={item.href}
                      className={isFirst ? styles.root : isLast ? styles.current : styles.parent}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <span
                      className={isFirst ? styles.root : isLast ? styles.current : styles.parent}
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {item.label}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
        </div>

        <div className={styles.rightGroup}>
          <form className={styles.searchForm} role="search" onSubmit={handleSearchSubmit}>
            <div className={styles.searchContainer}>
              <span className={styles.searchIcon} aria-hidden="true">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="search"
                className={styles.searchInput}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>

          <button
            type="button"
            className={styles.mobileSearchButton}
            aria-label="Search"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          <button
            type="button"
            className={styles.askAiButton}
            onClick={() => setIsAskAiOpen(true)}
            aria-label="Open Ask AI assistant"
          >
            <span aria-hidden="true">✦</span>
            <span>Ask AI</span>
          </button>

          <button
            type="button"
            className={styles.avatar}
            aria-label="User account: NK"
          >
            <span>NK</span>
          </button>
        </div>
      </header>

      <AskAiDrawer isOpen={isAskAiOpen} onClose={() => setIsAskAiOpen(false)} />
    </>
  );
};
